import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';

import { AuthProvider } from './contexts/AuthContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import Dashboard from './views/Dashboard';
import VacationCalendar from './views/VacationCalendar';
import AnnualView from './views/AnnualView';
import Workers from './views/Workers';
import VacationRequests from './views/VacationRequests';
import WorkCenters from './views/WorkCenters';
import Holidays from './views/Holidays';
import WorkCenterAnnualBags from './views/WorkCenterAnnualBags';
import Login from './views/Login';
import ChangePassword from './views/ChangePassword';
import InitialSetup from './views/InitialSetup';
import UserProfile from './views/UserProfile';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1a1a1a',
    },
    secondary: {
      main: '#666666',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: [
      'ForFutureSans',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2rem',
      fontWeight: 700, // Bold
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 700, // Bold
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 700, // Bold
      letterSpacing: '-0.01em',
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 500, // Medium
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 500, // Medium
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 500, // Medium
    },
    subtitle1: {
      fontWeight: 500, // Medium
    },
    subtitle2: {
      fontWeight: 500, // Medium
    },
    body1: {
      fontSize: '0.875rem',
      fontWeight: 400, // Regular
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.75rem',
      fontWeight: 300, // Light
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500, // Medium
      letterSpacing: '0.01em',
    },
    caption: {
      fontWeight: 300, // Light
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#333333',
          },
        },
        outlined: {
          borderColor: '#e0e0e0',
          color: '#1a1a1a',
          '&:hover': {
            borderColor: '#1a1a1a',
            backgroundColor: '#f5f5f5',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #e8e8e8',
        },
        elevation1: {
          boxShadow: 'none',
          border: '1px solid #e8e8e8',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#f8f8f8',
          fontWeight: 500,
          fontSize: '0.75rem',
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          borderBottom: '1px solid #e0e0e0',
        },
        body: {
          fontSize: '0.875rem',
          borderBottom: '1px solid #f0f0f0',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontSize: '0.75rem',
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1a1a1a',
          boxShadow: 'none',
          borderBottom: '1px solid #e8e8e8',
        },
      },
    },
  },
  spacing: 8,
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <SnackbarProvider>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/initial-setup" element={<InitialSetup />} />
              
              {/* Protected Routes */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/calendario"
                element={
                  <PrivateRoute>
                    <Layout>
                      <VacationCalendar />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/annual"
                element={
                  <PrivateRoute>
                    <Layout>
                      <AnnualView />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/workers"
                element={
                  <PrivateRoute requireAdmin>
                    <Layout>
                      <Workers />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/vacation-requests"
                element={
                  <PrivateRoute>
                    <Layout>
                      <VacationRequests />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/work-centers"
                element={
                  <PrivateRoute requireAdmin>
                    <Layout>
                      <WorkCenters />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/holidays"
                element={
                  <PrivateRoute requireAdmin>
                    <Layout>
                      <Holidays />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/annual-bags"
                element={
                  <PrivateRoute requireAdmin>
                    <Layout>
                      <WorkCenterAnnualBags />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <Layout>
                      <UserProfile />
                    </Layout>
                  </PrivateRoute>
                }
              />
            </Routes>
            </SnackbarProvider>
          </AuthProvider>
        </Router>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;